/**
 * W0 non-component helpers — the latest-plan selector, the status→i18n map, and
 * the New-plan wizard's event-type catalog. Kept in a JS-only module (no JSX)
 * so the component files stay fast-refresh-clean.
 */
import type { PlanOut, PlanStatus } from "@api/types";
import type { EventLetter } from "@lib/eventEnrichment";
import type { MessageKey } from "@i18n/messages";
import { asArray } from "@lib/asArray";

/**
 * The highest-version plan in a list (the current one), or null if none.
 * Tolerates a non-array input (a real backend can return `{}` / an error
 * envelope where an array is expected — the Oxbo-Z prod crash class).
 */
export function latestPlan(plans: unknown): PlanOut | null {
  const list = asArray<PlanOut>(plans);
  if (list.length === 0) return null;
  return [...list].sort((a, b) => b.version - a.version)[0] ?? null;
}

/** Status → i18n key (one per lifecycle state). */
export const STATUS_KEY: Record<PlanStatus, MessageKey> = {
  draft: "plan.status.draft",
  review: "plan.status.review",
  approved: "plan.status.approved",
  locked: "plan.status.locked",
  published: "plan.status.published",
};

/** Status → the pill's CSS modifier class (color band). */
export function statusClass(status: PlanStatus): string {
  if (status === "published") return "published";
  if (status === "draft") return "draft";
  return "review"; // review / approved / locked share the steel band
}

/** Event types offered in the New-plan wizard, each with conv + unconv codes. */
export interface NewPlanType {
  letter: EventLetter;
  codeConv: string;
  codeUnconv: string;
  titleKey: MessageKey;
  subKey: string;
}

export const NEWPLAN_TYPES: NewPlanType[] = [
  { letter: "P", codeConv: "PER", codeUnconv: "PNC", titleKey: "letter.P", subKey: "sub.P" },
  { letter: "T", codeConv: "TER", codeUnconv: "TFR", titleKey: "letter.T", subKey: "sub.T" },
  { letter: "W", codeConv: "WORK", codeUnconv: "WORK", titleKey: "letter.W", subKey: "sub.W" },
  { letter: "L", codeConv: "PULL", codeUnconv: "PULL", titleKey: "letter.L", subKey: "sub.L" },
];

/** Short ES sub-descriptions for the wizard cards (demo copy). */
export const NEWPLAN_SUB: Record<string, string> = {
  "sub.P": "Pozo nuevo / re-entry — hole plan, casing, programa",
  "sub.T": "Punzado, fractura (no conv.), puesta en producción",
  "sub.W": "Reparación / cambio de aparejo",
  "sub.L": "Intervención liviana con equipo de pulling",
};
